import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { categorias, reglas } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FolderTree, 
  Plus, 
  Edit2, 
  Trash2, 
  Wand2,
  Loader2,
  Save
} from 'lucide-react';

export default function Categorias() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categoriasList, setCategoriasList] = useState([]);
  const [reglasList, setReglasList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reglaDialogOpen, setReglaDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    color: '#BE123C',
    activa: true
  });

  const [reglaForm, setReglaForm] = useState({
    patron: '',
    categoria_id: '',
    activa: true
  });

  const [editingId, setEditingId] = useState(null);
  const [editingReglaId, setEditingReglaId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, reglasRes] = await Promise.all([
        categorias.getAll(),
        reglas.getAll()
      ]);
      setCategoriasList(catRes.data);
      setReglasList(reglasRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategoria = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await categorias.update(editingId, formData);
        toast.success('Categoría actualizada');
      } else {
        await categorias.create(formData);
        toast.success('Categoría creada');
      }
      loadData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Error al guardar categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRegla = async () => {
    if (!reglaForm.patron.trim() || !reglaForm.categoria_id) {
      toast.error('Patrón y categoría son requeridos');
      return;
    }

    setSaving(true);
    try {
      if (editingReglaId) {
        await reglas.update(editingReglaId, reglaForm);
        toast.success('Regla actualizada');
      } else {
        await reglas.create(reglaForm);
        toast.success('Regla creada');
      }
      loadData();
      setReglaDialogOpen(false);
      resetReglaForm();
    } catch (error) {
      toast.error('Error al guardar regla');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRegla = async (id) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    
    try {
      await reglas.delete(id);
      toast.success('Regla eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar regla');
    }
  };

  const resetForm = () => {
    setFormData({ nombre: '', color: '#BE123C', activa: true });
    setEditingId(null);
  };

  const resetReglaForm = () => {
    setReglaForm({ patron: '', categoria_id: '', activa: true });
    setEditingReglaId(null);
  };

  const openEditCategoria = (cat) => {
    setFormData({ nombre: cat.nombre, color: cat.color, activa: cat.activa });
    setEditingId(cat.id);
    setDialogOpen(true);
  };

  const openEditRegla = (regla) => {
    setReglaForm({ patron: regla.patron, categoria_id: regla.categoria_id, activa: regla.activa });
    setEditingReglaId(regla.id);
    setReglaDialogOpen(true);
  };

  const predefinedColors = [
    '#BE123C', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', 
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ];

  return (
    <div data-testid="categorias-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Categorías y Reglas
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona las categorías de gastos y reglas de clasificación automática
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        <Tabs defaultValue="categorias" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="categorias" className="gap-2">
              <FolderTree className="w-4 h-4" />
              Categorías
            </TabsTrigger>
            <TabsTrigger value="reglas" className="gap-2">
              <Wand2 className="w-4 h-4" />
              Reglas de Clasificación
            </TabsTrigger>
          </TabsList>

          {/* Categorías Tab */}
          <TabsContent value="categorias">
            <div className="flex justify-end mb-4">
              {isAdmin && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={resetForm} data-testid="add-categoria-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Categoría
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {editingId ? 'Editar Categoría' : 'Nueva Categoría'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input
                          value={formData.nombre}
                          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                          placeholder="Nombre de la categoría"
                          data-testid="categoria-nombre"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <div className="flex items-center gap-4">
                          <div className="flex gap-2 flex-wrap">
                            {predefinedColors.map((color) => (
                              <button
                                key={color}
                                className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                                  formData.color === color ? 'border-foreground scale-110' : 'border-transparent'
                                }`}
                                style={{ backgroundColor: color }}
                                onClick={() => setFormData({ ...formData, color })}
                              />
                            ))}
                          </div>
                          <Input
                            type="color"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Activa</Label>
                        <Switch
                          checked={formData.activa}
                          onCheckedChange={(v) => setFormData({ ...formData, activa: v })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveCategoria} disabled={saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="skeleton h-24 rounded-xl"></div>
                ))
              ) : (
                categoriasList.map((cat) => (
                  <Card 
                    key={cat.id} 
                    className={`card-hover cursor-pointer ${!cat.activa ? 'opacity-50' : ''}`}
                    onClick={() => isAdmin && openEditCategoria(cat)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${cat.color}20` }}
                          >
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: cat.color }}
                            />
                          </div>
                          <div>
                            <p className="font-medium">{cat.nombre}</p>
                            <Badge variant={cat.activa ? 'default' : 'secondary'} className="mt-1">
                              {cat.activa ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </div>
                        </div>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Reglas Tab */}
          <TabsContent value="reglas">
            <div className="flex justify-end mb-4">
              {isAdmin && (
                <Dialog open={reglaDialogOpen} onOpenChange={setReglaDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={resetReglaForm} data-testid="add-regla-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Regla
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {editingReglaId ? 'Editar Regla' : 'Nueva Regla'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Patrón de búsqueda</Label>
                        <Input
                          value={reglaForm.patron}
                          onChange={(e) => setReglaForm({ ...reglaForm, patron: e.target.value })}
                          placeholder="Ej: CFE, SAT, NOMINA"
                          data-testid="regla-patron"
                        />
                        <p className="text-xs text-muted-foreground">
                          Si el concepto contiene este texto, se asignará automáticamente la categoría
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Categoría</Label>
                        <Select 
                          value={reglaForm.categoria_id} 
                          onValueChange={(v) => setReglaForm({ ...reglaForm, categoria_id: v })}
                        >
                          <SelectTrigger data-testid="regla-categoria">
                            <SelectValue placeholder="Seleccionar categoría" />
                          </SelectTrigger>
                          <SelectContent>
                            {categoriasList.filter(c => c.activa).map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: cat.color }}
                                  />
                                  {cat.nombre}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Activa</Label>
                        <Switch
                          checked={reglaForm.activa}
                          onCheckedChange={(v) => setReglaForm({ ...reglaForm, activa: v })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setReglaDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveRegla} disabled={saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <Card className="table-container">
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="reglas-table">
                  <thead>
                    <tr>
                      <th>Patrón</th>
                      <th>Categoría</th>
                      <th>Estado</th>
                      {isAdmin && <th className="w-24">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          <td colSpan={4}>
                            <div className="skeleton h-6 w-full"></div>
                          </td>
                        </tr>
                      ))
                    ) : reglasList.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-12">
                          <Wand2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">No hay reglas de clasificación</p>
                        </td>
                      </tr>
                    ) : (
                      reglasList.map((regla) => (
                        <tr key={regla.id}>
                          <td>
                            <code className="bg-muted px-2 py-1 rounded text-sm">
                              {regla.patron}
                            </code>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ 
                                  backgroundColor: categoriasList.find(c => c.id === regla.categoria_id)?.color 
                                }}
                              />
                              {regla.categoria_nombre}
                            </div>
                          </td>
                          <td>
                            <Badge variant={regla.activa ? 'default' : 'secondary'}>
                              {regla.activa ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </td>
                          {isAdmin && (
                            <td>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => openEditRegla(regla)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteRegla(regla.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
